import { useEffect, useState } from "react";
import { History, BellRing } from "lucide-react";
import api from "../../api";

// What the session before this one left for it (GET /patients/<id>/previous-session):
// mode "plan" = its next-session plan (top of the Clinical Note tab),
// mode "followups" = its follow-ups and where they stand (Follow-Ups tab).
// View only; nothing is copied into this note.
const fmt = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const STATUS_STYLE = {
  done: "bg-emerald-100 text-emerald-800", sent: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800", failed: "bg-rose-100 text-rose-700", cancelled: "bg-secondary-100 text-secondary-500",
};

export default function PreviousSessionCarryover({ patientId, consultationId, appointmentId, mode }) {
  const [prev, setPrev] = useState(undefined);

  useEffect(() => {
    if (!patientId) return;
    api.getPreviousSession(patientId, { consultation_id: consultationId || undefined, appointment_id: appointmentId || undefined })
      .then((r) => setPrev(r.data?.previous || null)).catch(() => setPrev(null));
  }, [patientId, consultationId, appointmentId]);

  if (!prev) return null;
  const when = `${fmt(prev.date)}${prev.clinician ? ` · ${prev.clinician}` : ""}`;

  if (mode === "plan") {
    if (!prev.nextplan?.trim()) return null;
    return (
      <div className="mb-4 rounded-xl border border-primary-100 bg-primary-50/60 p-3">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary-700">
          <History className="w-3.5 h-3.5" /> Planned for this session <span className="font-normal normal-case tracking-normal text-secondary-500">· from the session on {when}</span>
        </p>
        <p className="mt-1 text-sm text-secondary-800 whitespace-pre-line">{prev.nextplan}</p>
      </div>
    );
  }

  if (!prev.follow_ups?.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-secondary-200 bg-secondary-50 p-3">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-secondary-600">
        <BellRing className="w-3.5 h-3.5" /> From last session <span className="font-normal normal-case tracking-normal text-secondary-500">· {when}</span>
      </p>
      <ul className="mt-2 space-y-1.5">
        {prev.follow_ups.map((f) => (
          <li key={f.id} className="flex flex-wrap items-start gap-2 text-sm">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[f.status] || "bg-secondary-100 text-secondary-700"}`}>{f.status_label}</span>
            <span className="text-xs text-secondary-500 mt-0.5">{f.kind === "patient_msg" ? "Message to patient" : "Your task"} · {fmt(f.trigger_date)}</span>
            <span className="basis-full text-secondary-800 whitespace-pre-line">{f.body || "-"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
